import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Box, TableContainer, TableHead, TableRow, TableCell, TableBody, Table, Paper, useTheme, Pagination, IconButton, TextField, Typography, Dialog } from '@mui/material';

import { Add } from '@mui/icons-material';
import { blue, grey } from '@mui/material/colors'

import { useAuth } from '../../../Auth/AuthContext';

import AlertComponent from '../Alert';
import RowTable from './RowTable';
import { ArrowBack } from '@mui/icons-material';

import DialogCreateVehiculo from './SubComponentsVehiculos/DialogCreateVehiculo';
import EmptyState from '../../common/EmptyState';
import { getTopSearchMatchIds } from '../../../utils/searchHighlights';
import { normalizeVehicleForList } from '../../../utils/vehicleEditing';

const API_URL = process.env.REACT_APP_API_URL;

function VehiculosPage({ handleChangePage }) {
  const { user } = useAuth();
  const areUserAdmin = user.rol === 'Administrador';

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [openAlert, setOpenAlert] = useState(false);
  const [openDialogCreate, setOpenDialogCreate] = useState(false);
  const [refresh, setRefresh] = useState(true);
  const handleRefreshData = useCallback(() => {
    setRefresh((prev) => !prev);
  }, []);

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const rowsPerPage = 30;

  const [searchTerm, setSearchTerm] = useState('');

  const [inputValue, setInputValue] = useState('');

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const deleteRequestsRef = useRef(new Set());

  // Cargar datos paginados del backend
  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    axios.get(`${API_URL}/historial/`, {
      params: {
        page: currentPage,
        search: searchTerm
      }
    })
    .then(response => {
      setCount(response.data.count);
      const pageVehiculos = response.data.results || response.data;
      // Ordenar de los más recientes a los más antiguos (por id)
      const sortedVehiculos = [...pageVehiculos].sort((a, b) => (b.id || 0) - (a.id || 0));
      setData(sortedVehiculos);
      setTotalPages(Math.ceil(response.data.count / rowsPerPage));
    })
    .catch(error => {
      setLoadError(true);
      console.error('Error al cargar vehículos:', error);
    })
    .finally(() => {
      setLoading(false);
    });
  }, [currentPage, refresh, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleDelete = useCallback((id) => {
    if (deleteRequestsRef.current.has(id)) return;
    deleteRequestsRef.current.add(id);
    axios.delete(`${API_URL}/historial/${id}/`)
      .then(() => {
        setData((prevData) => prevData.filter((item) => item.id !== id));
      })
      .catch((error) => {
        console.error('Error al eliminar el vehículo:', error);
      })
      .finally(() => {
        deleteRequestsRef.current.delete(id);
      });
  }, []);

  const handleInputChange = useCallback((event) => {
    setInputValue(event.target.value);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      setSearchTerm(inputValue);
    }
  }, [inputValue]);

  const currentData = useMemo(() => data, [data]);
  const searchHighlightIds = useMemo(() => new Set(getTopSearchMatchIds(
    currentData,
    searchTerm,
    ['id', 'placa', 'tipo_vehiculo', 'marca', 'modelo', 'color']
  )), [currentData, searchTerm]);

  const handleUpdateData = useCallback((updatedVehiculo) => {
    const normalizedVehicle = normalizeVehicleForList(updatedVehiculo);
    setData((prevData) => prevData.map((vehiculo) => vehiculo.id === normalizedVehicle.id ? normalizedVehicle : vehiculo));
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialogCreate(false);
  }, []);

  return (
    <Box sx={{ paddingTop: 1, position: 'relative', height: '100%' }}>

      <Dialog open={loading} fullWidth maxWidth="sm">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 4 }}>
          <Typography variant="h6" gutterBottom>
            Cargando datos...
          </Typography>
        </Box>
      </Dialog>

        <IconButton 
          color="primary" 
          sx={{
            zIndex: 1, // Asegura que esté por encima de otros elementos
          }}
          onClick={() => {handleChangePage('None')}}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant='h5' color='grey'>TOTAL DE VEHICULOS: {count}</Typography>
        <Typography variant='h5' color='grey'>TOTAL DE VEHICULOS EN ESTA PÁGINA: {data.length}</Typography>

        <TextField 
          label="Buscar Vehiculo Por Cualquier Dato (Presiona Enter)"
          variant="outlined"
          sx={{ paddingBottom: 2 }}
          fullWidth
          margin="normal"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650, borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead sx={{
            backgroundColor: isDarkMode ? grey[800] : blue[700],
            color: isDarkMode ? 'black' : 'white',
          }}>
            <TableRow>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>ID</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>PLACA</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>TIPO</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>MODELO</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>MARCA</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>COLOR</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', borderRight: '2px solid #ddd', fontWeight: 'bold', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                ACCIONES
                  <IconButton onClick={() => {setOpenDialogCreate(true)}}>
                     <Add sx={{ color: 'white' }}/>
                  </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentData.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState message={loadError ? 'No fue posible cargar los vehículos.' : searchTerm ? 'No se encontraron resultados.' : 'No hay vehículos para mostrar.'} />
                </TableCell>
              </TableRow>
            ) : currentData.map((item) => (
              <RowTable key={item.id} data={item} handleDelete={handleDelete} setOpenAlert={setOpenAlert} handleUpdateData={handleUpdateData} areUserAdmin={areUserAdmin} searchHighlighted={searchHighlightIds.has(item.id)}/>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Componente de Paginación */}
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
      <Pagination
        count={totalPages}
        page={currentPage}
        onChange={(e, value) => setCurrentPage(value)}
        color="primary"
      />
      </Box>

      <AlertComponent open={openAlert} setOpen={setOpenAlert} text='¡Todo ha salido bien!' severity='success' snack={true}/>
      <DialogCreateVehiculo openDialog={openDialogCreate} handleCloseDialog={handleCloseDialog} handleUpdateData={handleRefreshData}/>
    </Box>
  );
}

export default VehiculosPage;
